import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { CheckCircle, Shield, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FerpaWaiverForm = ({ onComplete }) => {
  const [waiverStatus, setWaiverStatus] = useState(null);
  const [waiverText, setWaiverText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [waiveRight, setWaiveRight] = useState(null);
  const [signature, setSignature] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    loadWaiverStatus();
  }, []);

  const loadWaiverStatus = async () => {
    try {
      const [statusRes, textRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ferpa/waiver`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/ferpa/waiver-text`, getAuthHeaders())
      ]);
      
      setWaiverStatus(statusRes.data);
      setWaiverText(textRes.data.waiver_text);
      
      if (statusRes.data.signed) {
        setWaiveRight(statusRes.data.waived);
        setSignature(statusRes.data.electronic_signature || '');
      }
    } catch (error) {
      console.error('Error loading FERPA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (waiveRight === null) {
      toast({ title: 'Error', description: 'Please select an option', variant: 'destructive' });
      return;
    }
    if (!signature.trim()) {
      toast({ title: 'Error', description: 'Please provide your electronic signature', variant: 'destructive' });
      return;
    }
    if (!agreedToTerms) {
      toast({ title: 'Error', description: 'Please agree to the terms', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/ferpa/waiver`,
        {
          waive_right: waiveRight,
          electronic_signature: signature,
          agreed_to_terms: true
        },
        getAuthHeaders()
      );
      
      toast({ title: 'Success', description: 'FERPA waiver submitted successfully' });
      loadWaiverStatus();
      if (onComplete) onComplete();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit waiver',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Already signed
  if (waiverStatus?.signed) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 mt-1" />
            <div>
              <h3 className="font-semibold text-green-800">FERPA Waiver Signed</h3>
              <p className="text-sm text-green-600 mt-1">
                You have {waiverStatus.waived ? 'waived' : 'retained'} your right to view recommendation letters.
              </p>
              <p className="text-xs text-green-600 mt-2">
                Signed by: {waiverStatus.electronic_signature}
              </p>
              <p className="text-xs text-green-600">
                Signed on: {new Date(waiverStatus.signed_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          FERPA Waiver
        </CardTitle>
        <CardDescription>
          Family Educational Rights and Privacy Act - Recommendation Letter Access
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Waiver Text */}
        <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-line max-h-64 overflow-y-auto">
          {waiverText}
        </div>

        {/* Options */}
        <div className="space-y-4">
          <Label className="text-base font-semibold">Your Decision</Label>
          <RadioGroup value={waiveRight?.toString()} onValueChange={(v) => setWaiveRight(v === 'true')}>
            <div className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              waiveRight === true ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <RadioGroupItem value="true" id="waive" className="mt-1" />
              <div>
                <Label htmlFor="waive" className="font-medium cursor-pointer">
                  I WAIVE my right to review all recommendations
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  <Badge variant="outline" className="mr-2 text-green-600 border-green-300">Recommended</Badge>
                  Shows trust in your recommenders and is viewed more favorably by admissions committees.
                </p>
              </div>
            </div>
            
            <div className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              waiveRight === false ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <RadioGroupItem value="false" id="retain" className="mt-1" />
              <div>
                <Label htmlFor="retain" className="font-medium cursor-pointer">
                  I DO NOT waive my right to review recommendations
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  You retain the right to view recommendations after enrollment.
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {waiveRight === false && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Note: Some admissions committees may give less weight to recommendations when the student has retained the right to view them.
            </p>
          </div>
        )}

        {/* Electronic Signature */}
        <div>
          <Label htmlFor="signature">Electronic Signature (type your full legal name)</Label>
          <Input
            id="signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Your Full Legal Name"
            className="mt-1"
          />
        </div>

        {/* Agreement */}
        <div className="flex items-start space-x-2">
          <Checkbox
            id="agree"
            checked={agreedToTerms}
            onCheckedChange={setAgreedToTerms}
          />
          <Label htmlFor="agree" className="text-sm cursor-pointer">
            I have read and understand the FERPA waiver statement above. I understand that my decision is voluntary and will apply to all colleges I apply to through this application.
          </Label>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || waiveRight === null || !signature.trim() || !agreedToTerms}
          className="w-full"
        >
          {submitting ? 'Submitting...' : 'Sign FERPA Waiver'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default FerpaWaiverForm;
